'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { useEffects } from '@/contexts/EffectsContext';
import { prefersReducedMotion } from '@/lib/effects';

export default function Preloader() {
  const ref = useRef<HTMLDivElement>(null);
  const { setLoaded } = useEffects();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduced = prefersReducedMotion();
    const letters = el.querySelectorAll<HTMLElement>('.pl-letter');
    const buro = el.querySelector<HTMLElement>('.pl-buro');
    const line = el.querySelector<HTMLElement>('.pl-line');

    const done = () => {
      document.body.classList.add('loaded');
      setLoaded(true);
      setTimeout(() => el.remove(), 900);
    };

    if (reduced) {
      gsap.set([letters, buro], { opacity: 1, y: 0 });
      gsap.set(el, { yPercent: -100 });
      done();
      return;
    }

    const tl = gsap.timeline({ onComplete: done });

    tl
      // loading line
      .fromTo(line, { scaleX: 0 }, { scaleX: 1, duration: 0.8, ease: 'power2.inOut' })
      // D  S  M letters
      .fromTo(letters,
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.55, stagger: 0.12, ease: 'power3.out' },
        0.3
      )
      // BURO
      .fromTo(buro,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.4, ease: 'power2.out' },
        '-=0.15'
      )
      // hold
      .to({}, { duration: 0.6 })
      // slide up
      .to(el, { yPercent: -100, duration: 0.85, ease: 'power4.inOut' });
  }, [setLoaded]);

  return (
    <div data-component-id="src/components/effects/Preloader.tsx:58:4" data-component-path="src/components/effects/Preloader.tsx" data-component-line="58" data-component-end-line="67" data-component-file="Preloader.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22preloader%20fixed%20inset-0%20z-%5B9999%5D%20flex%20flex-col%20items-center%20justify-center%20bg-background%22%7D" ref={ref} className="preloader fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background" aria-hidden>
      {/* loading line */}
      <div data-component-id="src/components/effects/Preloader.tsx:60:6" data-component-path="src/components/effects/Preloader.tsx" data-component-line="60" data-component-end-line="60" data-component-file="Preloader.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pl-line%20w-16%20h-%5B2px%5D%20bg-primary%20mb-8%20origin-left%22%7D" className="pl-line w-16 h-[2px] bg-primary mb-8 origin-left" style={{ transform: 'scaleX(0)' }} />
      <div data-component-id="src/components/effects/Preloader.tsx:61:6" data-component-path="src/components/effects/Preloader.tsx" data-component-line="61" data-component-end-line="66" data-component-file="Preloader.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-baseline%20gap-%5B0.05em%5D%22%7D" className="flex items-baseline gap-[0.05em]">
        {'DSM'.split('').map((ch, i) => (
          <span data-component-id="src/components/effects/Preloader.tsx:63:10" data-component-path="src/components/effects/Preloader.tsx" data-component-line="63" data-component-end-line="63" data-component-file="Preloader.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22pl-letter%20text-%5Bclamp(3rem%2C10vw%2C6rem)%5D%20font-outfit%20font-bold%20text-foreground%20opacity-0%22%7D" key={i} className="pl-letter text-[clamp(3rem,10vw,6rem)] font-outfit font-bold text-foreground opacity-0">{ch}</span>
        ))}
        <span data-component-id="src/components/effects/Preloader.tsx:65:8" data-component-path="src/components/effects/Preloader.tsx" data-component-line="65" data-component-end-line="65" data-component-file="Preloader.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22pl-buro%20text-%5Bclamp(3rem%2C10vw%2C6rem)%5D%20font-outfit%20font-bold%20text-primary%20opacity-0%22%2C%22textContent%22%3A%22BURO%22%7D" className="pl-buro text-[clamp(3rem,10vw,6rem)] font-outfit font-bold text-primary opacity-0">&nbsp;BURO</span>
      </div>
    </div>
  );
}
